"use client";

import React, { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  CodeIcon,
  Layout01Icon,
  ShoppingCart01Icon,
  FilesIcon,
  SmartPhone01Icon,
  HeartPulseIcon,
  CompassIcon,
  AnalyticsUpIcon,
  Database01Icon,
} from "@hugeicons/core-free-icons";
import { Card, Chip, Button } from "@heroui/react";
import Link from "next/link";

const ServiceArray = [
  {
    id: "website-development",
    name: "Website Development",
    description:
      "Building custom, high-performance websites from scratch using modern frameworks and standard-compliant practices. We specialize in fast load times, semantic structure, and responsive designs that scale effortlessly.",
    category: "Development",
    icon: CodeIcon,
    tags: [
      "Custom UI",
      "Next.js & React",
      "TailwindCSS",
      "API Integrations",
      "Performance Tuning",
    ],
    sub_services: [
      "Custom Web App Development",
      "Single Page Applications (SPAs)",
      "Server-Side Rendered (SSR) Systems",
      "Headless Architecture Setup",
    ],
  },
  {
    id: "ui-ux-design",
    name: "UI/UX Design",
    description:
      "Designing highly intuitive, user-centric interfaces. We translate complex user requirements into elegant, simple, and visually stunning digital products that delight users and convert visitors.",
    category: "Design",
    icon: Layout01Icon,
    tags: [
      "User Research",
      "Wireframing",
      "Figma Prototyping",
      "Design Systems",
      "Interaction Design",
    ],
    sub_services: [
      "User Flow Mapping",
      "High-Fidelity UI Prototyping",
      "Design System Development",
      "Usability Testing & Audits",
    ],
  },
  {
    id: "e-commerce-solutions",
    name: "E-commerce Solutions",
    description:
      "Developing online stores optimized for maximum conversions. We integrate robust shopping experiences, flexible payment gateways, inventory synchronization, and easy management dashboards.",
    category: "Development",
    icon: ShoppingCart01Icon,
    tags: [
      "Shopify & WooCommerce",
      "Stripe Integration",
      "Cart Optimization",
      "Secure Checkout",
    ],
    sub_services: [
      "Custom Online Storefronts",
      "Multi-Currency Payment Gateways",
      "Inventory & Order Workflows",
      "Customer Account Dashboards",
    ],
  },
  {
    id: "cms-development",
    name: "CMS & Headless Integration",
    description:
      "Empowering content editors with customizable, easy-to-use content management systems. From classic WordPress to modern headless CMS solutions, we give you full control over your content.",
    category: "Development",
    icon: FilesIcon,
    tags: [
      "WordPress Themes",
      "Sanity.io / Strapi",
      "Content Migration",
      "Dynamic Page Builders",
    ],
    sub_services: [
      "Custom Theme & Plugin Dev",
      "Headless CMS Architectures",
      "Dynamic Content Modeling",
      "Data Syncing & Backups",
    ],
  },
  {
    id: "mobile-app-development",
    name: "Mobile App Development",
    description:
      "Building seamless native and cross-platform mobile apps for iOS and Android. Engage your users on the go with fluid performance, push notifications, and robust offline capabilities.",
    category: "Development",
    icon: SmartPhone01Icon,
    tags: [
      "React Native",
      "Flutter",
      "iOS & Android",
      "Store Deployments",
      "Offline Syncing",
    ],
    sub_services: [
      "Cross-Platform App Dev",
      "Native Native Modules",
      "App Store Optimization Setup",
      "Push Notification Integration",
    ],
  },
  {
    id: "website-maintenance-support",
    name: "Maintenance & Support",
    description:
      "Ensuring your digital products remain secure, fast, and up-to-date. We proactively monitor performance, manage backups, run security audits, and resolve issues before they affect your users.",
    category: "Operations",
    icon: HeartPulseIcon,
    tags: [
      "Security Audits",
      "Hourly Backups",
      "Vulnerability Scans",
      "24/7 Monitoring",
    ],
    sub_services: [
      "Regular Core & Plugin Updates",
      "Performance Audits & Fixes",
      "Server Level Optimizations",
      "On-Call Technical Support",
    ],
  },
  {
    id: "web-strategy-consulting",
    name: "Strategy & Consulting",
    description:
      "Guiding your technology roadmap with expert advice. We help clarify project requirements, pick the right frameworks, design technical architectures, and build robust digital strategies.",
    category: "Strategy",
    icon: CompassIcon,
    tags: [
      "Tech Stack Audits",
      "Architecture Planning",
      "Feasibility Studies",
      "Product Strategy",
    ],
    sub_services: [
      "Discovery & Requirement Specs",
      "Technology Stack Advisory",
      "Scaling & Infrastructure Auditing",
      "Agile Project Roadmap Planning",
    ],
  },
  {
    id: "seo-optimization",
    name: "SEO Optimization",
    description:
      "Boosting organic visibility and ranking on search engine result pages. We optimize on-page structure, speed, content mapping, and technical setups to ensure high-intent traffic finds you.",
    category: "Growth",
    icon: AnalyticsUpIcon,
    tags: [
      "Keyword Mapping",
      "Technical SEO",
      "Rank Tracking",
      "Content Strategy",
    ],
    sub_services: [
      "Full On-Page & Code Optimization",
      "Core Web Vitals Improvements",
      "Schema Markup Implementation",
      "Performance Tracking Dashboards",
    ],
  },
  {
    id: "web-hosting-domains",
    name: "Hosting & Server Setup",
    description:
      "Deploying scalable web infrastructure. We design server setups using Vercel, Netlify, and AWS, manage domains, set up automated CI/CD deployment pipelines, and configure secure CDN networks.",
    category: "Operations",
    icon: Database01Icon,
    tags: [
      "Vercel & Netlify",
      "AWS / Cloudflare",
      "CI/CD Pipelines",
      "SSL & DNS Setup",
    ],
    sub_services: [
      "Cloud Architecture Design",
      "Automated Deploy Pipelines",
      "Global CDN & Caching Rules",
      "Domain and Security Certificate management",
    ],
  },
];

const categories = ["All", "Development", "Design", "Strategy", "Operations", "Growth"];

export default function Services() {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedService, setSelectedService] = useState(ServiceArray[0]);

  // Filter services by category
  const filteredServices = ServiceArray.filter(
    (service) => selectedCategory === "All" || service.category === selectedCategory
  );

  // Safely update active service if current is filtered out
  React.useEffect(() => {
    if (!filteredServices.some((s) => s.id === selectedService.id) && filteredServices.length > 0) {
      setSelectedService(filteredServices[0]);
    }
  }, [selectedCategory, filteredServices, selectedService]);

  return (
    <div className="min-h-screen py-24 md:py-32 w-full bg-neutral-50 dark:bg-[#08070F] text-neutral-900 dark:text-neutral-100 transition-colors duration-500 relative overflow-hidden">
      {/* Decorative Blur Backdrops */}
      <div className="absolute top-1/4 left-1/4 -translate-x-1/2 -translate-y-1/2 w-[350px] md:w-[600px] h-[350px] md:h-[600px] rounded-full bg-gradient-to-tr from-violet-600/10 to-indigo-600/10 dark:from-violet-900/15 dark:to-indigo-950/15 blur-[120px] pointer-events-none -z-10" />
      <div className="absolute bottom-1/4 right-1/4 translate-x-1/2 translate-y-1/2 w-[300px] md:w-[500px] h-[300px] md:h-[500px] rounded-full bg-gradient-to-tr from-blue-600/10 to-rose-600/10 dark:from-blue-900/15 dark:to-rose-950/15 blur-[120px] pointer-events-none -z-10" />

      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Modern Intro */}
        <div className="flex flex-col items-center text-center mb-16 md:mb-20">
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <Chip
              variant="soft"
              className="bg-violet-500/10 text-violet-600 dark:text-violet-400 font-outfit border border-violet-500/20 px-3 py-1 mb-4 text-xs font-semibold uppercase tracking-wider"
            >
              Our Offerings
            </Chip>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: "easeOut" }}
            className="text-4xl md:text-6xl font-extrabold tracking-tight mb-4 font-recoleta bg-gradient-to-r from-neutral-900 via-violet-900 to-indigo-900 dark:from-white dark:via-violet-200 dark:to-indigo-300 bg-clip-text text-transparent"
          >
            Solutions Tailored for Scale
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2, ease: "easeOut" }}
            className="text-neutral-500 dark:text-neutral-400 max-w-2xl text-lg font-outfit"
          >
            From custom design to scalable infrastructures, we deliver clean codes and performant user experiences. Explore our catalog below.
          </motion.p>
        </div>

        {/* Category Navigation - Custom sliding tab design */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex items-center gap-1.5 p-2 rounded-full bg-neutral-100 dark:bg-neutral-900/50 backdrop-blur-md border border-neutral-200/30 dark:border-neutral-800/30 overflow-x-auto scrollbar-hidden">
            {categories.map((cat) => {
              const isActive = selectedCategory === cat;
              return (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`relative flex items-center justify-center px-5 py-2.5 rounded-full text-sm font-semibold font-outfit transition-colors duration-300 outline-none cursor-pointer select-none ${
                    isActive
                      ? "text-white dark:text-white"
                      : "text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-200"
                  }`}
                >
                  {isActive && (
                    <motion.span
                      layoutId="active-category-tab"
                      className="absolute inset-0 rounded-full bg-violet-600 shadow-md shadow-violet-500/20 z-0"
                      transition={{ type: "spring", stiffness: 350, damping: 28 }}
                    />
                  )}
                  <span className="relative z-10 whitespace-nowrap">{cat}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Split Screen Catalog Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Navigation Column (Left) */}
          <div data-lenis-prevent className="lg:col-span-5 flex flex-col gap-3 max-h-[600px] lg:overflow-y-auto pr-0 lg:pr-3 scrollbar-thin scrollbar-thumb-neutral-200 dark:scrollbar-thumb-neutral-800">
            <AnimatePresence mode="popLayout">
              {filteredServices.map((service, index) => {
                const isSelected = selectedService.id === service.id;
                const Icon = service.icon;

                return (
                  <motion.div
                    key={service.id}
                    layoutId={`service-item-${service.id}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{
                      type: "spring",
                      stiffness: 380,
                      damping: 30,
                      delay: index * 0.03,
                    }}
                  >
                    <div
                      onClick={() => setSelectedService(service)}
                      className={`w-full text-left transition-all duration-300 relative overflow-hidden rounded-full cursor-pointer ${
                        isSelected
                          ? "bg-gradient-to-r from-violet-600/10 to-indigo-600/10 border-2 border-violet-500/40 shadow-md shadow-violet-500/5"
                          : "bg-white/40 dark:bg-neutral-900/30 border-2 border-neutral-200/50 dark:border-neutral-800/40 hover:bg-neutral-100/40 dark:hover:bg-neutral-900/50"
                      }`}
                    >
                      <div className="p-2 md:p-3 flex flex-row items-center gap-4 relative z-10">
                        {/* Selected Indicator Background Bubble */}
                        {/* {isSelected && (
                          <motion.div
                            layoutId="active-indicator"
                            className="absolute inset-0 bg-gradient-to-r from-violet-600/5 to-indigo-600/5 border-l-4 border-violet-600 dark:border-violet-500 pointer-events-none z-0"
                            transition={{ type: "spring", stiffness: 380, damping: 30 }}
                          />
                        )} */}

                        <div className={`p-3 rounded-full transition-all ${
                          isSelected 
                            ? "bg-violet-600 text-white" 
                            : "bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400 group-hover:scale-105"
                        }`}>
                          <HugeiconsIcon icon={Icon} className="h-6 w-6" size={24} />
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-semibold text-neutral-400 dark:text-neutral-500 font-outfit tracking-wider uppercase">
                              {service.category}
                            </span>
                          </div>
                          <h3 className="text-lg font-bold font-recoleta text-neutral-900 dark:text-white truncate">
                            {service.name}
                          </h3>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>

          {/* Details Column (Right) */}
          <div className="lg:col-span-7">
            <AnimatePresence mode="wait">
              <motion.div
                key={selectedService.id}
                initial={{ opacity: 0, x: 25, filter: "blur(8px)" }}
                animate={{ opacity: 1, x: 0, filter: "blur(0px)" }}
                exit={{ opacity: 0, x: -25, filter: "blur(8px)" }}
                transition={{ type: "spring", stiffness: 350, damping: 28 }}
                className="w-full"
              >
                <Card variant="transparent" className="bg-white/70 dark:bg-neutral-900/35 border border-neutral-200/50 dark:border-neutral-800/40 backdrop-blur-md rounded-3xl p-6 md:p-8 shadow-[0_12px_40px_rgba(0,0,0,0.03)] dark:shadow-none overflow-hidden relative">
                  {/* Internal Glow Effect */}
                  <div className="absolute top-0 right-0 w-64 h-64 bg-violet-600/5 dark:bg-violet-500/10 rounded-full blur-[80px] pointer-events-none" />

                  {/* Header info */}
                  <Card.Header className="p-0 mb-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className="p-4 rounded-2xl bg-gradient-to-br from-violet-600 to-indigo-600 text-white shadow-lg shadow-violet-500/20">
                        <HugeiconsIcon icon={selectedService.icon} className="h-8 w-8" size={32} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-xs font-bold text-violet-600 dark:text-violet-400 tracking-wider uppercase font-outfit">
                            {selectedService.category}
                          </span>
                        </div>
                        <h2 className="text-2xl md:text-3xl font-extrabold font-recoleta tracking-tight text-neutral-900 dark:text-white">
                          {selectedService.name}
                        </h2>
                      </div>
                    </div>
                  </Card.Header>

                  <Card.Content className="p-0">
                    <p className="text-neutral-600 dark:text-neutral-300 text-base md:text-lg leading-relaxed mb-8 font-outfit">
                      {selectedService.description}
                    </p>

                    {/* Sub-services / Features grid */}
                    <div className="mb-8">
                      <h4 className="text-sm font-semibold text-neutral-400 dark:text-neutral-500 uppercase tracking-wider mb-4 font-outfit">
                        What we offer in this service
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {selectedService.sub_services.map((sub, i) => (
                          <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.05 + 0.1 }}
                            whileHover={{ x: 4 }}
                            className="flex items-center gap-3 p-3 rounded-xl bg-neutral-100/50 dark:bg-neutral-800/20 border border-neutral-200/20 dark:border-neutral-800/30 transition-all cursor-default"
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-violet-600 dark:bg-violet-400 shrink-0" />
                            <span className="text-sm font-medium text-neutral-700 dark:text-neutral-200 font-outfit">
                              {sub}
                            </span>
                          </motion.div>
                        ))}
                      </div>
                    </div>

                    {/* Technology Tags */}
                    <div className="mb-8">
                      <h4 className="text-sm font-semibold text-neutral-400 dark:text-neutral-500 uppercase tracking-wider mb-3 font-outfit">
                        Core Frameworks & Methodology
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedService.tags.map((tag, idx) => (
                          <Chip
                            key={idx}
                            variant="soft"
                            className="bg-neutral-100 dark:bg-neutral-800/60 text-neutral-600 dark:text-neutral-300 border-none font-outfit text-xs px-2.5 py-0.5"
                          >
                            {tag}
                          </Chip>
                        ))}
                      </div>
                    </div>

                    {/* Contact CTA */}
                    <div className="flex justify-end pt-4 border-t border-neutral-200/50 dark:border-neutral-800/40">
                      <Link href="/contact" className="inline-block">
                        <Button
                          className="bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-outfit font-semibold px-6 py-6 rounded-xl shadow-lg shadow-violet-500/20 hover:shadow-violet-500/30 transition-all duration-300 cursor-pointer"
                        >
                          Inquire about {selectedService.name}
                        </Button>
                      </Link>
                    </div>
                  </Card.Content>
                </Card>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}
